import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'orderBy'
})
export class OrderByPipe implements PipeTransform {
  transform(value: any | any[], expression?: any, reverse?: boolean, priorityKey?: string): any {
    if (value == null) {
      return value;
    }

    const isArray = Array.isArray(value);

    if (isArray) {
      return this.sortArray(value, expression, !!reverse, priorityKey);
    }

    return value;
  }

  /**
   * Sort array (returns new array copy)
   */
  private sortArray(value: any[], expression?: any, reverse = false, priorityKey?: string): any[] {
    // copy to avoid mutating original
    const arr = value.slice();

    const isDeepLink = expression && typeof expression === 'string' && expression.indexOf('.') !== -1;

    let parsedExpression: string[] | null = null;
    if (isDeepLink) {
      parsedExpression = this.parseExpression(expression as string);
    }

    const isPriorityDeepLink =
      priorityKey && typeof priorityKey === 'string' && priorityKey.indexOf('.') !== -1;

    let parsedPriority: string[] | null = null;
    if (isPriorityDeepLink) {
      parsedPriority = this.parseExpression(priorityKey as string);
    }

    arr.sort((a: any, b: any): number => {
      //priority always first
      if (priorityKey) {
        const ap = isPriorityDeepLink ? this.getValue(a, parsedPriority) : a?.[priorityKey];
        const bp = isPriorityDeepLink ? this.getValue(b, parsedPriority) : b?.[priorityKey];

        const aPinned = !!ap;
        const bPinned = !!bp;

        if (aPinned !== bPinned) {
          return aPinned ? -1 : 1;
        }
      }

      // 2) original sort (полностью прежняя логика)
      if (!expression) {
        return this.safeCompare(a, b);
      }

      if (!isDeepLink) {
        const key = expression as string;
        const res = this.safeCompare(a?.[key], b?.[key]);
        return reverse ? -res : res;
      }

      const va = this.getValue(a, parsedExpression);
      const vb = this.getValue(b, parsedExpression);
      const res = this.safeCompare(va, vb);
      return reverse ? -res : res;
    });

    return arr;
  }

  /**
   * Parse expression, split into items
   */
  private parseExpression(expression: string): string[] {
    if (!expression || typeof expression !== 'string') {
      return [];
    }
    // convert [key] to .key and split by dots
    expression = expression.replace(/\[(\w+)\]/g, '.$1');
    expression = expression.replace(/^\./, '');
    return expression.split('.');
  }

  /**
   * Get value by expression (safe). If any part is null/undefined => returns undefined.
   */
  private getValue(object: any, expression: string[] | null): any {
    if (!expression || expression.length === 0) {
      return object;
    }

    if (object == null) {
      return undefined;
    }

    let cur = object;
    for (let i = 0; i < expression.length; i++) {
      const key = expression[i];

      // if cur is null/undefined or not an object, return undefined
      if (cur == null || (typeof cur !== 'object' && typeof cur !== 'function')) {
        return undefined;
      }

      // if property doesn't exist, return undefined
      if (!(key in cur)) {
        return undefined;
      }

      cur = cur[key];
    }

    return cur;
  }

  /**
   * Safe compare that handles null/undefined, numbers, dates and strings (localeCompare for strings)
   * null/undefined are considered smaller than any other value.
   */
  private safeCompare(a: any, b: any): number {

    const isBoolOrNull = (v: any) => v === true || v === false || v == null;
    if (isBoolOrNull(a) && isBoolOrNull(b)) {
      // true -> 0, false -> 1, null -> 2
      const rank = (v: any) => (v === true ? 0 : v === false ? 1 : 2);
      const ra = rank(a);
      const rb = rank(b);
      return ra === rb ? 0 : (ra > rb ? 1 : -1);
    }

    // both null/undefined
    if (a === null && b === null) {
      return 0;
    }
    if (a === null) {
      return -1;
    }
    if (b === null) {
      return 1;
    }

    // Dates
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime() ? 0 : (a.getTime() > b.getTime() ? 1 : -1);
    }

    // Numbers (or numeric strings)
    const aNum = typeof a === 'number' ? a : (typeof a === 'string' && !isNaN(Number(a)) ? Number(a) : null);
    const bNum = typeof b === 'number' ? b : (typeof b === 'string' && !isNaN(Number(b)) ? Number(b) : null);
    if (aNum !== null && bNum !== null) {
      return aNum === bNum ? 0 : (aNum > bNum ? 1 : -1);
    }

    // Strings - use localeCompare for proper alphabetical order.
    if (typeof a === 'string' && typeof b === 'string') {
      // case-insensitive compare — change if case-sensitive needed
      const res = a.localeCompare(b, undefined, { sensitivity: 'base' });
      return res === 0 ? 0 : (res > 0 ? 1 : -1);
    }

    // fallback generic comparison
    try {
      if (a === b) {
        return 0;
      }
      return a > b ? 1 : -1;
    } catch (e) {
      return 0;
    }
  }
}
